// DeleteContactConfirmationModal — stateless leaf extracted from
// ContactEditModal.tsx renderDeleteConfirmationModal closure (Stage 2).
// Pure render of props: holds no useState/useEffect/useRef of its own.
// The orchestrator still owns the `deleteConfirmation` state + the
// useModalOverlayClick ref/handlers and passes them in. DOM/markup byte-identical.
import type { RefObject } from 'react';

export function DeleteContactConfirmationModal({
  deleteConfirmation,
  contentRef,
  onOverlayClick,
  onOverlayMouseDown,
  onCancel,
  onDelete,
  onClose,
}: {
  deleteConfirmation: { contactId: string; name: string } | null;
  contentRef: RefObject<HTMLDivElement>;
  onOverlayClick: (e: React.MouseEvent) => void;
  onOverlayMouseDown: (e: React.MouseEvent) => void;
  onCancel: () => void;
  onDelete?: (id: string) => void | Promise<void>;
  onClose: () => void;
}) {
  if (!deleteConfirmation) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 20000,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '25vh 16px 16px',
      }}
      onMouseDown={onOverlayMouseDown}
      onClick={onOverlayClick}
    >
      <div
        ref={contentRef}
        style={{
          backgroundColor: 'var(--color-surface)',
          borderRadius: '24px',
          maxWidth: '400px',
          width: '100%',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ padding: '24px' }}>
          <p style={{
            fontSize: '16px',
            color: 'var(--color-text-muted)',
            lineHeight: 1.6,
            margin: 0,
          }}>
            Are you sure you want to permanently delete this card? <strong>This action cannot be undone.</strong>
          </p>
        </div>
        <div style={{
          padding: '30px 24px 16px',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '15px',
        }}>
          <button
            onClick={onCancel}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.2s',
              background: 'var(--color-action-cancel-bg)',
              border: '1px solid var(--color-action-cancel-border)',
              color: 'var(--color-action-cancel-text)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--color-action-cancel-bg-hover)';
              e.currentTarget.style.borderColor = 'var(--color-action-cancel-border-hover)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'var(--color-action-cancel-bg)';
              e.currentTarget.style.borderColor = 'var(--color-action-cancel-border)';
            }}
          >
            Cancel
          </button>
          <button
            onClick={async () => {
              if (onDelete) {
                try {
                  await onDelete(deleteConfirmation.contactId);
                  onCancel();
                  onClose();
                } catch (err) {
                  console.error('[ContactEditModal] Delete failed:', err);
                  alert('Failed to delete contact. Please try again.');
                  onCancel();
                }
              } else {
                onCancel();
                onClose();
              }
            }}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.2s',
              background: 'var(--color-danger)',
              border: 'none',
              color: 'white',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--color-action-delete-text)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'var(--color-danger)';
            }}
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}
